import { useCallback, useEffect, useState } from 'react';
import { toast } from '@heroui/react';
import { apiFetch } from '@/lib/api';
import { downloadBlob } from '@/lib/download';
import type { Client, WorkerRow } from '@/lib/types';
import { useUserStore } from '@/store/userSlice';

export interface WorkersImportResult {
  imported: number;
  errors: string[];
}

export interface WorkersQuery {
  page?: number;
  search?: string;
  clientId?: string | null;
}

const DEFAULT_LIMIT = 15;

async function readError(res: Response): Promise<string> {
  const data = (await res.json().catch(() => null)) as { error?: string } | null;
  return data?.error ?? `Ошибка запроса (${res.status})`;
}

function exportStamp(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(
    now.getHours(),
  )}-${pad(now.getMinutes())}`;
}

export function useWorkersPage() {
  const isStaff = useUserStore((s) => s.isStaff);

  const [items, setItems] = useState<WorkerRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(DEFAULT_LIMIT);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [clientFilter, setClientFilter] = useState<string | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isImportOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState<WorkersImportResult | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  // Список клиентов — только для фильтра персонала
  useEffect(() => {
    if (!isStaff) return;
    void apiFetch('/api/v1/admin/clients')
      .then((res) => (res.ok ? res.json() : []))
      .then((list: Client[]) => setClients(list))
      .catch(() => setClients([]));
  }, [isStaff]);

  const fetchWorkers = useCallback(
    async (query?: WorkersQuery) => {
      const params = new URLSearchParams();
      params.set('page', String(query?.page ?? page));
      params.set('limit', String(limit));
      const searchValue = query?.search ?? search;
      if (searchValue) params.set('search', searchValue);
      const clientValue = query?.clientId !== undefined ? query.clientId : clientFilter;
      if (clientValue) params.set('client_id', clientValue);

      setIsLoading(true);
      setError(null);
      try {
        const res = await apiFetch(`/api/v1/admin/workers/?${params.toString()}`);
        if (!res.ok) throw new Error(await readError(res));
        const data = (await res.json()) as {
          items: WorkerRow[];
          total: number;
          page: number;
          limit: number;
        };
        setItems(data.items);
        setTotal(data.total);
        setPage(data.page);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Не удалось загрузить исполнителей');
      } finally {
        setIsLoading(false);
      }
    },
    [page, limit, search, clientFilter],
  );

  useEffect(() => {
    void fetchWorkers();
  }, [fetchWorkers]);

  const handleSearchSubmit = () => {
    const value = searchInput.trim();
    setSearch(value);
    setPage(1);
    void fetchWorkers({ page: 1, search: value });
  };

  const handleClientFilterChange = (clientId: string | null) => {
    setClientFilter(clientId);
    setPage(1);
    void fetchWorkers({ page: 1, clientId });
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    void fetchWorkers({ page: nextPage });
  };

  const handleOpenImport = () => {
    setImportResult(null);
    setImportOpen(true);
  };

  const handleCloseImport = () => {
    setImportOpen(false);
    setImportResult(null);
  };

  const handleDownloadSample = async () => {
    try {
      const res = await apiFetch('/api/v1/admin/workers/import/sample');
      if (!res.ok) throw new Error(await readError(res));
      downloadBlob(await res.blob(), 'workers-import-sample.xlsx');
    } catch (e) {
      toast.danger(e instanceof Error ? e.message : 'Не удалось скачать пример');
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (isStaff && clientFilter) params.set('client_id', clientFilter);
      const suffix = params.toString() ? `?${params.toString()}` : '';
      const res = await apiFetch(`/api/v1/admin/workers/export${suffix}`);
      if (!res.ok) throw new Error(await readError(res));
      downloadBlob(await res.blob(), `workers-export-${exportStamp()}.xlsx`);
    } catch (e) {
      toast.danger(e instanceof Error ? e.message : 'Не удалось экспортировать исполнителей');
    }
  };

  const handleImport = async (file: File) => {
    setIsImporting(true);
    try {
      // Админ импортирует в клиента из фильтра (иначе — обязательный query-параметр)
      const params = new URLSearchParams();
      if (isStaff && clientFilter) params.set('client_id', clientFilter);
      const suffix = params.toString() ? `?${params.toString()}` : '';
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiFetch(`/api/v1/admin/workers/import${suffix}`, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const message = await readError(res);
        toast.danger(message);
        setImportResult({ imported: 0, errors: [message] });
        return;
      }

      const result = (await res.json()) as WorkersImportResult;
      void fetchWorkers();
      if (result.errors.length === 0) {
        setImportOpen(false);
        setImportResult(null);
        toast.success(`Импортировано исполнителей: ${result.imported}`);
        return;
      }
      setImportResult(result);
      toast.warning(`Импортировано: ${result.imported}, ошибок: ${result.errors.length}`, {
        description: 'Подробности — в окне импорта',
      });
    } catch {
      const message = 'Ошибка сети. Проверьте подключение.';
      toast.danger(message);
      setImportResult({ imported: 0, errors: [message] });
    } finally {
      setIsImporting(false);
    }
  };

  return {
    isStaff,
    items,
    total,
    page,
    limit,
    clients,
    clientFilter,
    searchInput,
    isLoading,
    error,
    setSearchInput,
    handleSearchSubmit,
    handleClientFilterChange,
    handlePageChange,
    isImportOpen,
    importResult,
    isImporting,
    handleOpenImport,
    handleCloseImport,
    handleDownloadSample,
    handleExport,
    handleImport,
  };
}
